/* eslint-disable jsx-a11y/anchor-is-valid */
import { useLocation, useNavigate } from 'react-router-dom';
import { SetCreditNoteListAtom, GetCreditNoteListAtom, CreditNoteState } from '../../store/CreditNoteStore';
import { useCallback, useContext, useEffect, useState } from 'react';
import RestartAlt from '@mui/icons-material/RestartAlt';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import { creditNoteService } from '../../services/creditNoteService';
import { Table, Input } from 'antd';
import CreditNoteFilter from './CreditNoteFilter';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import { formatCustomDate } from '../../helpers/formatCustomDate';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import LogicContext from '../../Context/LogicContext';
import { isArray } from 'lodash';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';
import { CreditNote } from './interface/CreditNote.Interface';
import StatusBadge from '../../generals/StatusBadge';

const { Search } = Input;

// Define interfaces for data structures

interface FormData {
  from_date: string;
  to_date: string;
}

function CreditNoteMainPage() {
  const { pathname } = useLocation();
  const setCreditNoteListAtom = SetCreditNoteListAtom();
  const getCreditNoteListAtom: CreditNoteState | undefined = GetCreditNoteListAtom();
  const navigate = useNavigate();
  const url = pathname.split('/');
  const currModuleId: string = url[1];
  const currModuleName: string = url[2];
  const currUserMenu: string = url[3];
  const [filteredData, setFilteredData] = useState<CreditNote[] | any[]>([]);
  const logicContext = useContext(LogicContext);
  const [searchText, setSearchText] = useState<string>('');

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    document.title = 'Credit note page';
    if (!getCreditNoteListAtom.credit_note?.length) {
      loadTableList();
    } else {
      setFilteredData(getCreditNoteListAtom?.credit_note);
    }
  }, [getCreditNoteListAtom.credit_note]);

  useEffect(() => {
    if (sessionStorage.getItem('reloadPage') === 'true') {
      sessionStorage.removeItem('reloadPage');
      reloadData();
    }
  }, []);
 
  const reloadData = useCallback(() => {
    loadTableList();
  }, []);

  const loadTableList = useCallback(() => {
    let isMounted = true;
    creditNoteService.creditNoteList().then((res: { data?: { credit_note: CreditNote[] } }) => {
      const serverData = toLowercaseKeys(res.data);
      if (isMounted && isArray(serverData?.credit_note)) {
        setCreditNoteListAtom(serverData);
        setFilteredData(serverData?.credit_note);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [setCreditNoteListAtom]);

  const onFilterSelect = useCallback(
    (selection: number) => {
      if (selection == 0) {
        setFilteredData(getCreditNoteListAtom?.credit_note);
        return;
      }
      const newList = getCreditNoteListAtom?.credit_note?.filter(item => item?.status_id == selection);
      setFilteredData(newList?.length ? newList : []);
    },
    [getCreditNoteListAtom]
  );

  const addNew = useCallback(() => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
  }, [navigate, currModuleId, currModuleName, currUserMenu]);

  const columns = [
    {
      title: 'Ref_No',
      key: 'ref_no',
      dataIndex: 'ref_no',
      sorter: (a: CreditNote, b: CreditNote) => (a?.ref_no ?? '') - (b?.ref_no ?? ''),
    },
    {
      title: 'Number',
      key: 'credit_note_number',
      dataIndex: 'credit_note_number',
      className: 'text-primary',
      sorter: (a: CreditNote, b: CreditNote) => (a?.credit_note_number ?? '').localeCompare(b?.credit_note_number ?? ''),
    },
    {
      title: 'Name',
      dataIndex: 'customer_full_name',
      sorter: (a: CreditNote, b: CreditNote) => (a?.customer_full_name ?? '').localeCompare(b?.customer_full_name ?? ''),
    },
    {
      title: 'Customer Email',
      dataIndex: 'customer_email_address',
      sorter: (a: CreditNote, b: CreditNote) => (a?.customer_email_address ?? '').localeCompare(b?.customer_email_address ?? ''),
    },
    {
      title: 'Customer Mobile',
      dataIndex: 'customer_mobile_no',
      sorter: (a: CreditNote, b: CreditNote) => (a?.customer_mobile_no ?? '').localeCompare(b?.customer_mobile_no ?? ''),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      sorter: (a: CreditNote, b: CreditNote) => (a?.amount ?? 0) - (b?.amount ?? 0),
      render: (record: number) => <span> {formatCustomNumber(record)} </span>,
    },
    {
      title: 'Status',
      dataIndex: 'status_name',
      sorter: (a: CreditNote, b: CreditNote) => (a?.status_name ?? '').localeCompare(b?.status_name ?? ''),
      render: (record: string, row: CreditNote) => (
        <StatusBadge statusId={row?.status_id} statusName={record} />
      ),
    },
    {
      title: 'Date',
      dataIndex: 'transaction_date',
      sorter: (a: CreditNote, b: CreditNote) => (a?.transaction_date ?? '').localeCompare(b?.transaction_date ?? ''),
      render: (record: string) => <span> {formatCustomDate(record)} </span>,
    },
  ];

  const handleRowClick = useCallback(
    (record: CreditNote) => {
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${record?.ref_no}`);
    },
    [navigate, currModuleId, currModuleName, currUserMenu]
  );

  const setSelectedRows = useCallback((sel: any) => {
  }, []);

  const searchByDate = async () => {
    const formData: FormData = getValues();
    const response = await creditNoteService.creditNoteByDate(formData);
    const serverData =  toLowercaseKeys(response.data);
    if (serverData) {
      setFilteredData(serverData?.credit_note);
    } else {
      logicContext.showToast('No result found', '');
      setFilteredData([]);
    }
  };

  const handleTableSearch = useCallback(
    (text: string) => {
      setSearchText(text);

      if (text.trim() === '') {
        setFilteredData(getCreditNoteListAtom?.credit_note || []);
        return;
      }

      const searchTerm = text.toLowerCase();
      const filtered = (getCreditNoteListAtom?.credit_note || []).filter(credit =>
        Object.values(credit).some(value => value?.toString().toLowerCase().includes(searchTerm))
      );

      setFilteredData(filtered);
    },
    [getCreditNoteListAtom]
  );

  const onClearInput = useCallback(() => {
    setSearchText('');
    setFilteredData(getCreditNoteListAtom?.credit_note || []);
  }, [getCreditNoteListAtom]);

  return (
    <>
      <section className="mt-8 px-6">
        <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg shadow-sm p-4">
          <div className="flex items-center">
            <Tooltip title="Reload">
              <RestartAlt
                className="cursor-pointer text-blue-600 hover:text-blue-800 transition-colors h-10 w-10 p-1"
                onClick={reloadData}
                color="primary"
                fontSize="large"
              />
            </Tooltip>
          </div>
          <div className="flex justify-end">
            <button
              onClick={addNew}
              className="h-10 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              ADD NEW
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <CreditNoteFilter
              bulkActionData={getCreditNoteListAtom?.status_info}
              onFilterSelect={onFilterSelect}
              className="h-10 w-40 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
            <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-semibold text-white bg-blue-600 rounded-full">
              {filteredData?.length}
            </span>
          </div>

          <div className="flex flex-row items-end space-x-2">
            <div>
              <span className="ml-1 text-sm">From Date:</span>
              <input
                defaultValue={moment(Date.now()).format('YYYY-MM-DD')}
                {...register('from_date')}
                className="bg-white ring-1 ring-gray-200 w-full pl-3 rounded max-h-full bottom-0 outline-none border-transparent focus:border-transparent focus:ring-0"
                type="date"
              />
            </div>
            <div>
              <span className="ml-1 text-sm">To Date:</span>
              <input
                {...register('to_date')}
                defaultValue={moment(Date.now()).format('YYYY-MM-DD')}
                className="bg-white ring-1 ring-gray-200 w-full pl-3 rounded max-h-full bottom-0 outline-none border-transparent focus:border-transparent focus:ring-0"
                type="date"
              />
            </div>
            <div>
              <button onClick={searchByDate} type="button" className="text-sm text-white px-3 bg-blue-500 rounded py-2.5">
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <div className="flex flex-col w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search in table"
              value={searchText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTableSearch(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Backspace') {
                  onClearInput();
                }
              }}
              className="bg-white ring-1 ring-gray-200 w-full pl-3 py-2 rounded outline-none focus:ring-blue-300"
            />
          </div>
        </div>
      </section>

      <section className="mt-5 w-full">
        <div>
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredData?.map(item => ({
              ...item,
              Amount: formatCustomNumber(item.amount),
              Transaction_Date: formatCustomDate(item.transaction_date),
            }))}
            allowCheckBox={true}
            setSelectedRows={setSelectedRows}
            rowKey={(record: CreditNote | any) => record.Ref_No}
            handleRowClick={(record: any) => handleRowClick(record)}
          />
        </div>
      </section>
    </>
  );
}

export default CreditNoteMainPage;
